import React from 'react';
import { Box, Button, Typography } from '@material-ui/core';

interface AppCrashNoticeProps {
  error?: Error | null;
  resetError: () => void;
}

/**
 * Shown when the top-level ErrorBoundary catches a render failure.
 *
 * The previous fallback rendered the small "Error detected" toast, whose text
 * read "App continues running. Error logged automatically." That was false in
 * the one situation it appeared: the boundary wraps the router, so a render
 * throw unmounts the entire dashboard. A clinician was left with a blank page
 * and a reassuring note in the corner — no vitals, no alarms, and nothing
 * saying the monitoring had stopped.
 *
 * This says what happened and offers the one action that reliably fixes it.
 */
const defaultProps = {
  error: null,
};

export const AppCrashNotice: React.FC<AppCrashNoticeProps> = ({ error, resetError }) => (
  <Box
    alignItems="center"
    bgcolor="#b71c1c"
    color="white"
    display="flex"
    flexDirection="column"
    justifyContent="center"
    minHeight="100vh"
    p={3}
    textAlign="center"
    data-testid="app-crash"
  >
    <Typography variant="h5" gutterBottom>
      Monitoring has stopped
    </Typography>
    <Typography style={{ maxWidth: 520 }} paragraph>
      This screen is no longer showing live patient data. Do not rely on it.
      Reload to resume monitoring, and use the bedside monitors until it comes
      back.
    </Typography>

    <Box display="flex" style={{ gap: 12 }} mt={1}>
      <Button
        variant="contained"
        onClick={() => window.location.reload()}
        data-testid="app-crash-reload"
      >
        Reload now
      </Button>
      <Button variant="outlined" style={{ borderColor: 'white', color: 'white' }} onClick={resetError}>
        Try to continue
      </Button>
    </Box>

    {process.env.NODE_ENV === 'development' && error && (
      <Box
        bgcolor="rgba(0,0,0,0.35)"
        borderRadius={4}
        fontFamily="monospace"
        fontSize={12}
        maxWidth={720}
        mt={3}
        p={2}
        textAlign="left"
        style={{ overflowX: 'auto' }}
      >
        {error.message}
      </Box>
    )}
  </Box>
);

AppCrashNotice.defaultProps = defaultProps;

export default AppCrashNotice;
